import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { SortableContainer, SortableHandle } from 'react-sortable-hoc';
import styled, { css } from 'styled-components';

import EffectPicker from './EffectPicker';
import EffectSettingsPopover from './EffectSettingsPopover';
import FiltersList from './FiltersList';

import { getStoreState, storeDispatch } from '../state/Store';
import * as Actions from '../state/actions';
import Layer from '../data/Layer';
import { Frame } from '../data/Frame';
import SortEvent from './SortEvent';
import {
  styledWithProps,
  selectLocalFiles,
  generateClassName,
  hasParentWithClassName
} from '../utils';
import Tooltip from './components/ButtonTooltip';
import { LayerRowOptionsMenu } from './LayerRowOptionsMenu';
import { reportIssue } from '../utils/Tracker';
import { ButtonIcon } from './components/ButtonIcon';
import { LayerTimelineView } from './LayerTimelineView';
import { FrameType } from '../data/FrameType';
import { TextSettingsPopover } from './settings-popovers/TextSettingsPopover';
import FileType from '../data/FileType';
import { SolidColorSettingsPopover } from './settings-popovers/SolidColorSettingsPopover';
import { SolidFrame } from '../data/SolidFrame';
import { TextFrame } from '../data/TextFrame';
import { GradientSettingsPopover } from './settings-popovers/GradientSettingsPopover';
import { GradientFrame } from '../data/GradientFrame';
import { FramesThumbnails } from './FramesThumbnails';
import { MAX_FRAMES_AMOUNT } from '../data/Config';
import { SoundWaveSettingsPopover } from './settings-popovers/SoundWaveSettingsPopover';
import { SoundWaveFrame } from '../data/SoundWaveFrame';
import { Effect } from '@beatflyer/shaders-lib';
import { CompositionEffect } from 'compose/CompositionEffect';
import { CaptionsFrame } from 'captions/CaptionsFrame';
import { CaptionsDisplayMode } from 'captions/CaptionsDisplayMode';
import { ShapeFrame } from 'data/ShapeFrame';
import { ShapeSettingsPopover } from './settings-popovers/ShapeSettingsPopover';
import { ShapeData } from 'data/FlyerData';
import { EditGradientColorsCommand } from 'state/commands/EditGradientColorsCommand';
import { EditSolidColorCommand } from 'state/commands/EditSolidColorCommand';
import { RemoveEffectCommand } from 'state/commands/RemoveEffectCommand';
import { SortEffectsCommand } from 'state/commands/SortEffectsCommand';
import { ReplacePictureFrameCommand } from 'state/commands/ReplacePictureFrameCommand';
import { ChangeShapeCommand } from 'state/commands/ChangeShapeCommand';
import { EditSoundWavePropertyCommand } from 'state/commands/EditSoundWavePropertyCommand';
import { ChangeTextCommand } from 'state/commands/ChangeTextCommand';
import { TextStyleUpdate } from 'data/TextStyleUpdate';
import { ChangeTextStylesCommand } from 'state/commands/ChangeTextStylesCommand';
import { ChangeCaptionDisplayModeCommand } from 'state/commands/ChangeCaptionDisplayModeCommand';
import { ChangeCaptionAnimationCommand } from 'state/commands/ChangeCaptionAnimationCommand';
import { ChangeCaptionHaveBackgroundCommand } from 'state/commands/ChangeCaptionsHaveBackgroundCommand';
import { ChangeCaptionBackgroundCommand } from 'state/commands/ChangeCaptionsBackgroundCommand';
import { SortFramesCommand } from 'state/commands/SortFramesCommand';
import { PlatformType } from 'data/PlatformType';
import { EditGradientRotationCommand } from 'state/commands/EditGradientRotationCommand';

const SortableFiltersContainer = SortableContainer(FiltersList);
const SortableFramesContainer = SortableContainer(FramesThumbnails);

const Header = styled.div`
  position: relative;
  padding: 10px;
  display: flex;
  align-items: center;
`;

const DraggableHeader = SortableHandle(Header);

interface WrapperProps {
  sorting: boolean;
}
const Wrapper = styledWithProps<WrapperProps>()(styled.div)`
  border-top: solid var(--layer-border-width) var(--layer-border-color);
  position: relative;
  overflow: visible;

  button.icon.icon {
    margin-left: 6px;
  }
  button.icon.icon--left-margin-auto {
    margin-left: auto;
  }

  hr {
    border: none;
    height: 30px;
    margin-left: 6px;
    border-left: var(--layer-border-width) solid var(--layer-border-color);
  }

  button.icon.icon--delete > div {
    background-color: var(--bright-red);
  }

  button.icon.icon--options {
    margin-right: 0;
    margin-right: calc(var(--layer-border-width) * -1);
  }
  ${(props: WrapperProps) =>
    props.sorting &&
    css`
      cursor: grabbing;
      * {
        cursor: grabbing;
      }
    `};
  }
`;

interface Props {
  data: Layer;
  pickEffect: boolean;
  getPlaybackPosition: () => number;
  onAddEffectSelect: (id: string, data: any) => void;
  onAddFilter: (layer: Layer, el: HTMLElement) => void;
  isEditingTimeline: boolean;
}

interface State {
  editableEffect: Effect | null;
  editFrame: boolean;
  optionMenuOpen: boolean;
  isOver: boolean;
  isSortingEffects: boolean;
  isSortingFrames: boolean;
}

export default class extends React.Component<Props> {
  effectBrowsingCache: any;
  state: State = {
    editFrame: false,
    optionMenuOpen: false,
    editableEffect: null,
    isOver: false,
    isSortingEffects: false,
    isSortingFrames: false
  };
  settingsTrigger: HTMLElement;
  menuBtn = React.createRef<HTMLButtonElement>();
  editFrameBtn = React.createRef<HTMLButtonElement>();
  replaceFrameBtn = React.createRef<HTMLButtonElement>();
  hadFrameSelected = false;
  previouslySelectedFrame: Frame | null = null;
  el = React.createRef<HTMLDivElement>();

  constructor(props: Props) {
    super(props);

    this.effectBrowsingCache = {};
  }

  componentWillUnmount() {
    if (this.state.editableEffect && this.props.pickEffect) {
      this.stopListeningToEffectsScroll();
    }
    document.removeEventListener('mousedown', this.didClickOnDocument);
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (prevState.editableEffect !== this.state.editableEffect) {
      this.stopListeningToEffectsScroll();
      if (this.state.editableEffect && this.props.pickEffect) {
        this.listenToEffectsScroll();
      }
    }
    if (
      prevProps.pickEffect !== this.props.pickEffect &&
      !this.props.pickEffect
    ) {
      // console.log('DELETE EFFECTS CACHE');
      this.effectBrowsingCache = {};
    }
    const selectedFrame = getStoreState().app.selectedFrame;
    if (selectedFrame !== this.previouslySelectedFrame) {
      document.removeEventListener('mousedown', this.didClickOnDocument);
      this.previouslySelectedFrame?.off(
        'start-editing',
        this.selectedFrameStartedEditing
      );
      const containsSelectedFrame =
        selectedFrame !== null &&
        this.props.data.frames.includes(selectedFrame);
      if (containsSelectedFrame) {
        document.addEventListener('mousedown', this.didClickOnDocument);
        if (!selectedFrame?.hasBeenEdited) {
          this.setState({ editFrame: true });
        }
        selectedFrame?.on('start-editing', this.selectedFrameStartedEditing);
        this.hadFrameSelected = true;
      } else {
        if (this.hadFrameSelected) {
          this.previouslySelectedFrame!.hasBeenEdited = true;
        }
        this.hadFrameSelected = false;
      }
    }
    this.previouslySelectedFrame = selectedFrame;
  }

  didClickOnDocument = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    const canvas = document.querySelector('.showcase-item');
    if (canvas && canvas.contains(target)) {
      return;
    }
    if (
      this.editFrameBtn.current &&
      this.editFrameBtn.current.contains(target)
    ) {
      // console.log('INSIDE EDIT BTN');
      return;
    }
    if (
      this.replaceFrameBtn.current &&
      this.replaceFrameBtn.current.contains(target)
    ) {
      return;
    }
    if (this.el.current) {
      if (
        this.el.current
          .querySelector('.frame-settings-popover')
          ?.contains(target)
      ) {
        // console.log('INSIDE FRAME SETTINGS');
        return;
      }
    }
    // check if it's clicked an already selected frame
    if (hasParentWithClassName(target, 'frame-thumb--selected')) {
      // console.log('IS ALREADY SELECTED FRAME');
      return;
    }

    if (document.querySelector('.chrome-picker')) {
      return;
    }

    if (document.querySelector('.frame-settings-popover')) {
      return;
    }

    storeDispatch(Actions.unselectFrame());
  };

  selectedFrameStartedEditing = () => {
    this.setState({ editFrame: true });
  };

  get hasMultipleFrames(): boolean {
    return this.props.data.frames.length > 1;
  }

  listenToEffectsScroll() {
    const el = ReactDOM.findDOMNode(this) as HTMLElement;
    const effectsList = el.querySelector('.effect-picker .effects-groups');
    if (effectsList) {
      effectsList.addEventListener('scroll', this.didScrollEffectPicker);
    } else {
      reportIssue('Cannot find effects list');
    }
  }

  stopListeningToEffectsScroll() {
    const el = ReactDOM.findDOMNode(this) as HTMLElement;
    const effectsList = el.querySelector('.effect-picker .effects-groups');
    if (effectsList) {
      effectsList.removeEventListener('scroll', this.didScrollEffectPicker);
    }
  }

  didScrollEffectPicker = () => {
    // console.log('SCROLL');
    this.setState({ editableEffect: null });
  };

  cacheEffectProperties(effect: Effect | CompositionEffect) {
    const id = Object.getPrototypeOf(effect).constructor.id;
    this.effectBrowsingCache[id] = JSON.stringify(effect.toObject());
    // console.log('cached ' + id + ': ' + this.effectBrowsingCache[id]);
  }

  didSelectFrame = (frame: Frame) => {
    storeDispatch(Actions.selectFrame(frame));
  };

  replaceSingleFrame = async (frame: Frame) => {
    const canReplaceWithVideo = this.props.data.frames.length === 1;
    const selectType = canReplaceWithVideo ? FileType.Graphic : FileType.Image;
    const files = await selectLocalFiles(selectType);
    if (files.length) {
      storeDispatch(
        new ReplacePictureFrameCommand(this.props.data.id, frame.id, files[0])
      );
    }
  };

  didClickReplaceFrame = () => {
    this.replaceSingleFrame(getStoreState().app.selectedFrame!);
  };

  editFrame = () => {
    this.setState({ editFrame: true });
  };
  didCloseEditPanel = () => {
    this.setState({ editFrame: false });
  };

  onMenuOpenChange = () => {
    this.setState({ optionMenuOpen: !this.state.optionMenuOpen });
  };
  showEffectSettings = (target: HTMLElement, effect: Effect) => {
    this.settingsTrigger = target;
    this.setState({
      editableEffect: effect
    });
  };
  removeEditableEffect = () => {
    const effect = this.state.editableEffect;
    if (effect) {
      this.setState(
        {
          editableEffect: null
        },
        () => {
          storeDispatch(new RemoveEffectCommand(this.props.data.id, effect.id));
        }
      );
    }
  };

  onEffectPickerSelect = (id: string) => {
    if (getStoreState().flyer.previewedEffect) {
      this.cacheEffectProperties(getStoreState().flyer.previewedEffect!);
    }
    let cachedData = null;
    if (this.effectBrowsingCache[id]) {
      console.log('HAS CACHE for ' + id);
      cachedData = JSON.parse(this.effectBrowsingCache[id]);
    }
    this.props.onAddEffectSelect(id, cachedData);
  };

  onTextChange = (value: string) => {
    const { selectedFrame } = getStoreState().app;
    if (selectedFrame && selectedFrame instanceof TextFrame) {
      storeDispatch(new ChangeTextCommand(selectedFrame.id, value));
    }
  };

  onTextStyleChange = (prop: string, change: TextStyleUpdate) => {
    const { selectedFrame } = getStoreState().app;
    if (selectedFrame && selectedFrame instanceof TextFrame) {
      storeDispatch(
        new ChangeTextStylesCommand(selectedFrame.id, prop, change)
      );
    }
  };

  onEditCaptions = () => {
    this.setState({ editFrame: false });
    storeDispatch(Actions.unselectFrame());
    storeDispatch(Actions.editCaptions());
  };

  onEditCaptionsMode = (mode: CaptionsDisplayMode) => {
    const { selectedFrame } = getStoreState().app;
    if (selectedFrame && selectedFrame instanceof CaptionsFrame) {
      storeDispatch(
        new ChangeCaptionDisplayModeCommand(selectedFrame.id, mode)
      );
    }
  };

  onEditCaptionAnimation = (value: boolean) => {
    const { selectedFrame } = getStoreState().app;
    if (selectedFrame && selectedFrame instanceof CaptionsFrame) {
      storeDispatch(new ChangeCaptionAnimationCommand(selectedFrame.id, value));
    }
  };

  editCaptionsHaveBackground = (value: boolean) => {
    const { selectedFrame } = getStoreState().app;
    if (selectedFrame && selectedFrame instanceof CaptionsFrame) {
      storeDispatch(
        new ChangeCaptionHaveBackgroundCommand(selectedFrame.id, value)
      );
    }
  };

  edidCaptionsBackground = (color: string) => {
    const { selectedFrame } = getStoreState().app;
    if (selectedFrame && selectedFrame instanceof CaptionsFrame) {
      storeDispatch(
        new ChangeCaptionBackgroundCommand(selectedFrame.id, color)
      );
    }
  };

  onSolidColorChange = (color: string) => {
    const { selectedFrame } = getStoreState().app;
    if (selectedFrame && selectedFrame instanceof SolidFrame) {
      storeDispatch(
        new EditSolidColorCommand(selectedFrame.id, selectedFrame.color, color)
      );
    }
  };

  onGradientColorsChange = (colors: string[]) => {
    const { selectedFrame } = getStoreState().app;
    if (
      selectedFrame &&
      (selectedFrame instanceof GradientFrame ||
        selectedFrame instanceof ShapeFrame)
    ) {
      storeDispatch(
        new EditGradientColorsCommand(
          selectedFrame.id,
          [...selectedFrame.colors],
          [...colors]
        )
      );
    }
  };

  onGradientRotationChange = (value: number) => {
    const { selectedFrame } = getStoreState().app;
    if (
      selectedFrame &&
      (selectedFrame instanceof GradientFrame ||
        selectedFrame instanceof ShapeFrame)
    ) {
      storeDispatch(new EditGradientRotationCommand(selectedFrame.id, value));
    }
  };

  onShapeChange = (data: ShapeData) => {
    const { selectedFrame } = getStoreState().app;
    if (selectedFrame && selectedFrame instanceof ShapeFrame) {
      storeDispatch(new ChangeShapeCommand(selectedFrame.id, data));
    }
  };

  onEditShape = () => {
    const { selectedFrame } = getStoreState().app;
    if (selectedFrame && selectedFrame instanceof ShapeFrame) {
      storeDispatch(Actions.editShape(selectedFrame));
    }
  };

  onSoundWavePropertyChange = (prop: string, value: any) => {
    const { selectedFrame } = getStoreState().app;
    if (selectedFrame && selectedFrame instanceof SoundWaveFrame) {
      storeDispatch(
        new EditSoundWavePropertyCommand(selectedFrame.id, prop, value)
      );
    }
  };

  resetEditableEffect = () => {
    storeDispatch(Actions.resetEffect(this.state.editableEffect));
  };

  onEffectSettingsClose = () => {
    this.setState({ editableEffect: null });
  };

  addFilter = () => {
    this.props.onAddFilter(
      this.props.data,
      ReactDOM.findDOMNode(this) as HTMLElement
    );
  };

  onSortEffectsStart = () => {
    this.setState({ isSortingEffects: true });
  };

  onSortFilters = (e: SortEvent) => {
    this.setState({ isSortingEffects: false });
    if (e.oldIndex === e.newIndex) {
      return;
    }
    storeDispatch(
      new SortEffectsCommand(this.props.data.id, e.oldIndex, e.newIndex)
    );
  };

  onSortFrames = (e: SortEvent) => {
    this.setState({ isSortingFrames: false });
    if (e.oldIndex === e.newIndex) {
      return;
    }
    storeDispatch(
      new SortFramesCommand(this.props.data.id, e.oldIndex, e.newIndex)
    );
  };

  renderActions = (): React.ReactElement | null => {
    const { frames, visible } = this.props.data;
    const { selectedFrame, controllerHasConfirmation } = getStoreState().app;
    const hasFrameSelected =
      selectedFrame && frames.indexOf(selectedFrame) > -1;
    if (controllerHasConfirmation) {
      return null;
    }
    return (
      <>
        {hasFrameSelected && (
          <>
            {selectedFrame && selectedFrame.isEditable ? (
              <Tooltip text="Edit frame style" hide={this.state.editFrame}>
                <ButtonIcon
                  icon="edit"
                  active={this.state.editFrame}
                  ref={this.editFrameBtn}
                  onClick={this.editFrame}
                  modifiers={{ leftMarginAuto: true }}
                />
              </Tooltip>
            ) : (
              <Tooltip text="Replace frame">
                <ButtonIcon
                  icon="replace"
                  ref={this.replaceFrameBtn}
                  onClick={this.didClickReplaceFrame}
                  modifiers={{ leftMarginAuto: true }}
                />
              </Tooltip>
            )}
            {/* <Tooltip text="Delete frame">
              <ButtonIcon icon="delete" onClick={this.didClickRemoveFrame} />
            </Tooltip> */}
            <hr />
          </>
        )}
        <>
          {visible && (
            <Tooltip text="Add effect">
              <ButtonIcon
                icon="effect"
                onClick={this.addFilter}
                modifiers={{ leftMarginAuto: !hasFrameSelected }}
              />
            </Tooltip>
          )}
          <Tooltip text="Layer options" hide={this.state.optionMenuOpen}>
            <ButtonIcon
              icon="options"
              active={this.state.optionMenuOpen}
              ref={this.menuBtn}
              onClick={this.onMenuOpenChange}
              modifiers={{ leftMarginAuto: !visible }}
            />
          </Tooltip>
        </>
      </>
    );
  };

  renderFrameSettings(): JSX.Element | null {
    const { selectedFrame } = getStoreState().app;
    if (!this.state.editFrame || !selectedFrame) {
      return null;
    }
    switch (selectedFrame.type) {
      case FrameType.Text:
        const textFrame = selectedFrame as TextFrame;
        return (
          <TextSettingsPopover
            trigger={this.editFrameBtn.current}
            text={textFrame.text}
            textStyle={textFrame.textStyle}
            onChangeText={this.onTextChange}
            onStyleChange={this.onTextStyleChange}
            onClose={this.didCloseEditPanel}
          />
        );
      case FrameType.Captions:
        const captionsFrame = selectedFrame as CaptionsFrame;
        const captions = getStoreState().flyer.captions;
        return (
          <TextSettingsPopover
            trigger={this.editFrameBtn.current}
            text=""
            textStyle={captionsFrame.textStyle}
            isCaptions={true}
            hasCaptions={captions !== null}
            onChangeText={this.onTextChange}
            onStyleChange={this.onTextStyleChange}
            onClose={this.didCloseEditPanel}
            onEditCaptions={this.onEditCaptions}
            captionsDisplayMode={captionsFrame.displayMode}
            captionsAnimated={captionsFrame.animated}
            captionsHaveBackground={captionsFrame.hasBackground}
            onCaptionsHaveBackgroundChange={this.editCaptionsHaveBackground}
            captionsBackgroundColor={captionsFrame.backgroundColor}
            onCaptionsBackgroundChange={this.edidCaptionsBackground}
            onDisplayModeChange={this.onEditCaptionsMode}
            onCaptionAnimationChange={this.onEditCaptionAnimation}
          />
        );
      case FrameType.SolidColor:
        return (
          <SolidColorSettingsPopover
            trigger={this.editFrameBtn.current}
            color={(selectedFrame as SolidFrame).color}
            onChange={this.onSolidColorChange}
            onClose={this.didCloseEditPanel}
          />
        );
      case FrameType.Gradient:
        const gradientFrame = selectedFrame as GradientFrame;
        return (
          <GradientSettingsPopover
            trigger={this.editFrameBtn.current}
            colors={gradientFrame.colors}
            rotation={gradientFrame.rotation}
            onChange={this.onGradientColorsChange}
            onRotationChange={this.onGradientRotationChange}
            onClose={this.didCloseEditPanel}
          />
        );
      case FrameType.Shape:
        const shapeFrame = selectedFrame as ShapeFrame;
        return (
          <ShapeSettingsPopover
            trigger={this.editFrameBtn.current}
            data={shapeFrame.mask}
            colors={shapeFrame.colors}
            onChange={this.onGradientColorsChange}
            onShapeChange={this.onShapeChange}
            onEditShape={this.onEditShape}
            onClose={this.didCloseEditPanel}
          />
        );
      case FrameType.SoundWave:
        return (
          <SoundWaveSettingsPopover
            trigger={this.editFrameBtn.current}
            mesh={(selectedFrame as SoundWaveFrame).meshSpectrum}
            onChange={this.onSoundWavePropertyChange}
            onClose={this.didCloseEditPanel}
          />
        );
      default:
        return null;
    }
  }

  shouldCancelFiltersSorting = (
    e: React.MouseEvent<any, MouseEvent>
  ): boolean => {
    if (document.querySelector('ul.menu')) {
      return true;
    }
    if (document.querySelector('.custom-property')) {
      return true;
    }
    return false;
  };

  render() {
    const { isBeatflyerLite, platform } = getStoreState().app;
    const { isEditingTimeline, data, pickEffect } = this.props;
    const hasEffects = data.effects && data.effects.length > 0;

    const items = this.props.data.effects.filter(
      effect => !effect.isPreviewing
    ) as (Effect | CompositionEffect)[];
    if (data.compositionEffect) {
      items.push(data.compositionEffect);
    }

    let canEditFrame = true;
    if (isBeatflyerLite) {
      canEditFrame = platform === PlatformType.Figma;
    }

    return (
      <Wrapper
        onMouseEnter={e => this.setState({ isOver: true })}
        onMouseLeave={e => this.setState({ isOver: false })}
        sorting={this.state.isSortingEffects || this.state.isSortingFrames}
        className={generateClassName('layer-row', {
          hidden: !this.props.data.visible || this.props.data.locked
        })}
        innerRef={this.el}
      >
        <DraggableHeader>
          <SortableFramesContainer
            layer={this.props.data}
            getPlaybackPosition={this.props.getPlaybackPosition}
            onSelect={
              !canEditFrame || pickEffect ? undefined : this.didSelectFrame
            }
            onEdit={!canEditFrame || pickEffect ? undefined : this.editFrame}
            canAddFrames={
              !isBeatflyerLite &&
              !data.isVideo &&
              data.frames.length < MAX_FRAMES_AMOUNT &&
              !isEditingTimeline &&
              !pickEffect
            }
            isOver={this.state.isOver}
            onSortStart={() => this.setState({ isSortingFrames: true })}
            onSortEnd={this.onSortFrames}
            axis="x"
            lockAxis="x"
            isSorting={this.state.isSortingFrames}
            distance={1}
          />
          {this.renderActions()}
          {this.state.optionMenuOpen && (
            <LayerRowOptionsMenu
              layer={this.props.data}
              hasMultipleFrames={this.hasMultipleFrames}
              getParent={() => this.menuBtn.current as HTMLElement}
              onClose={this.onMenuOpenChange}
              hasEffects={hasEffects}
            />
          )}
        </DraggableHeader>
        {isEditingTimeline ? (
          <LayerTimelineView layer={this.props.data} />
        ) : (
          <>
            <SortableFiltersContainer
              items={items}
              layer={this.props.data}
              onSortStart={this.onSortEffectsStart}
              onSortEnd={this.onSortFilters}
              lockAxis="y"
              editableEffect={this.state.editableEffect}
              onShowSettings={this.showEffectSettings}
              distance={1}
              shouldCancelStart={this.shouldCancelFiltersSorting}
            />
            {
              this.props.pickEffect && (
                <EffectPicker
                  layer={this.props.data}
                  editableEffect={this.state.editableEffect}
                  onSelect={this.onEffectPickerSelect}
                  onShowSettings={this.showEffectSettings}
                />
              ) /* : (
              <div style={{ padding: '0 10px' }}>
                <ActionButton
                  className="add-effect-btn"
                  style={{
                    margin: '3px -2px',
                    paddingLeft: '25px'
                  }}
                  onClick={this.addFilter}
                  icon="effect"
                >
                  Add effect
                </ActionButton>
              </div>
                )*/
            }
            {this.state.editableEffect && (
              <EffectSettingsPopover
                getTrigger={() => this.settingsTrigger}
                effect={this.state.editableEffect}
                onClose={this.onEffectSettingsClose}
                onReset={this.resetEditableEffect}
                onRemove={this.removeEditableEffect}
              />
            )}
            {this.renderFrameSettings()}
          </>
        )}
      </Wrapper>
    );
  }
}



// WEBPACK FOOTER //
// ./src/controller/LayerRow.tsx